import { useEffect, useMemo, useRef, useState } from 'react';
import { isInternetExplorer } from '../../utils/browser-detection';
import sleep from '../../utils/sleep';
export var BOTTOM_SHEET_ANIMATE_S = 0.5;
/**
 * Animates an absolute positioned component sliding up from the bottom of the screen.
 *
 * @param ref - `RefType` - the ref of the sliding component
 * @param slideDownCb - `() => void` - callback to call after slide down animation
 *
 * @returns object - `style` is the style override for the sliding object.
 *                   `slideDown` is the function that animates slide down
 *                   `refBg` is the ref object to pass into the background
 *
 * Implementation details (slide up sheet):
 * - Set bottom to `null` initially (along with a CSS `transition` definition)
 * - Calculate the height of the component after render using `ref`
 * - Set the inline style with `{ bottom: -height }`
 * - Add a CSS class with `{ bottom: 0 !important }` to the component
 * - The component will now animate the slide up (from `-height` to `0`)
 *
 * Implementation details (slide up background):
 * - Add a CSS class with `{ background: rgb(0,0,0,0.5) !important; }` to animate opacity
 *   transition from transparent to gray.
 *
 * Implementation details (slide down):
 * - Remove dynamically added CSS classes from background and slide up component
 * - Wait for the animation to complete and then call the `slideDownCb` function
 */
export default function useAnimationSlideUp(ref, slideDownCb) {
    var SHEET_CLASS = 'dc-slideUp';
    var BG_CLASS = 'dc-slideUp-bg';
    var _a = useState(0), height = _a[0], setHeight = _a[1];
    // Set height after a tick to ensure full render and accurate height measurement
    // https://github.com/underdog-tech/link/pull/2482
    setTimeout(function () { var _a; return setHeight((_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.clientHeight); });
    // On initialize, add CSS for class "dc-slideUp" to DOM
    useEffect(function () {
        var styleId = 'dc-slideUp-ID';
        // Ensure stylesheet element is only added once
        if (document.getElementById(styleId))
            return;
        var style = document.createElement('style');
        style.id = styleId;
        style.innerHTML = "\n      .".concat(SHEET_CLASS, " { bottom: 0 !important; }\n      .").concat(BG_CLASS, " { background: rgb(0,0,0,0.5) !important; }");
        document.getElementsByTagName('head')[0].appendChild(style);
    });
    // The bottom CSS attribute. Should always be 0 for IE11.
    // Should be -height if height is available now.
    // Should be -1000 (ensure off screen) if height is not yet calculated.
    var bottom = isInternetExplorer() ? 0 : -height || -1000;
    // When height and bottom are calculated, add SHEET_CLASS class to the component.
    // Check for bottom since we want to add SHEET_CLASS class *after* bottom is set inline.
    useEffect(function () {
        var _a;
        if (height && bottom) {
            (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.classList.add(SHEET_CLASS);
        }
    }, [bottom, height, ref]);
    var style = useMemo(function () { return ({ bottom: bottom, transition: "bottom ".concat(BOTTOM_SHEET_ANIMATE_S, "s") }); }, [bottom]);
    // https://github.com/underdog-tech/link/pull/2481
    var refBg = useRef(null);
    var slideDown = function () {
        ref === null || ref === void 0 ? void 0 : ref.current.classList.remove(SHEET_CLASS);
        refBg === null || refBg === void 0 ? void 0 : refBg.current.classList.remove(BG_CLASS);
        return sleep(BOTTOM_SHEET_ANIMATE_S).then(slideDownCb);
    };
    useEffect(function () { return refBg === null || refBg === void 0 ? void 0 : refBg.current.classList.add(BG_CLASS); }, [refBg]);
    return { style: style, slideDown: slideDown, refBg: refBg };
}
